import React, { useContext, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { createModal } from "react-modal-promise";
import GeneralModal from "../../helpers/GeneralModal";
import LogicContext from "../../Context/LogicContext";
import GeneralFileUploadPreview from "../../components/GeneralFileUploadPreview";
import { bankingService } from "../../services/bankingService";
import { GetManualJournalAtom } from "../../store/manualJounalAtom";
import {
    GetFundTransferAtom,
    SetFundTransferAtom,
    GetFundTransferEditAtom,
    SetFundTransferEditAtom,
} from "../../store/fundTransferAtom";

const TRANSFER_TYPES = [
    { transaction_type_id: 19, transaction_name: "Transfer To Another Account" },
    { transaction_type_id: 20, transaction_name: "Receive From Another Account" },
];

const inputCls = "w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition";
const selectCls = "w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition appearance-none";

const FormField = ({ label, children, required, error }) => (
    <div>
        <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">
            {label}{required && <span className="text-red-400 ml-0.5">*</span>}
        </label>
        {children}
        {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
    </div>
);

const emptyFormValues = {
    journal_date: moment().format('YYYY-MM-DD'),
    reference_number: '',
    from_account_id: '',
    to_account_id: '',
    notes: '',
    currency_id: '',
    amount: '',
};

function AddFundTransferPage() {
    const navigate = useNavigate();

    const logicContext = useContext(LogicContext);
    const myPromiseModal = createModal(GeneralModal);

    const getFundTransferAtom = GetFundTransferAtom();
    const setFundTransferAtom = SetFundTransferAtom();
    const fundTransferEdit = GetFundTransferEditAtom();
    const setFundTransferEdit = SetFundTransferEditAtom();
    const journalAtom = GetManualJournalAtom();

    const isEditMode = !!fundTransferEdit?.account_ref_no;

    const [transactionTypeId, setTransactionTypeId] = useState(fundTransferEdit?.transaction_type_id || 19);
    const [saving, setSaving] = useState(false);
    const [attachmentFile, setAttachmentFile] = useState(null);
    const [existingAttachmentUrl, setExistingAttachmentUrl] = useState('');
    const [fileKey, setFileKey] = useState(0);

    const { register, handleSubmit, reset, formState: { errors } } = useForm({
        defaultValues: emptyFormValues,
    });

    useEffect(() => {
        document.title = isEditMode ? 'Edit Fund Transfer' : 'New Fund Transfer';
        if (!getFundTransferAtom?.trans_rec_chart_of_accounts?.length) {
            bankingService.fundTransferList().then(response => {
                const serverData = response?.data;
                setFundTransferAtom({
                    fund_transfer: serverData?.fund_transfer?.length ? serverData.fund_transfer : [],
                    trans_rec_chart_of_accounts: serverData?.trans_rec_chart_of_accounts?.length ? serverData.trans_rec_chart_of_accounts : [],
                });
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (isEditMode) {
            setTransactionTypeId(fundTransferEdit.transaction_type_id);
            reset({
                journal_date: moment(fundTransferEdit.journal_date).format('YYYY-MM-DD'),
                reference_number: String(fundTransferEdit.reference_number || ''),
                from_account_id: fundTransferEdit.from_account_id ? String(fundTransferEdit.from_account_id) : '',
                to_account_id: fundTransferEdit.to_account_id ? String(fundTransferEdit.to_account_id) : '',
                notes: fundTransferEdit.notes || '',
                currency_id: fundTransferEdit.currency_id ? String(fundTransferEdit.currency_id) : '',
                amount: fundTransferEdit.amount || '',
            });
            setExistingAttachmentUrl(fundTransferEdit.attachment_url || '');
        }

        return () => {
            setFundTransferEdit(null);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const groupedAccountOptions = useMemo(() => {
        const groups = {};
        (getFundTransferAtom?.trans_rec_chart_of_accounts || []).forEach(acc => {
            const key = acc.parent_account_id;
            if (!groups[key]) groups[key] = { label: acc.parent_account_name || String(key), accounts: [] };
            groups[key].accounts.push(acc);
        });
        return Object.values(groups);
    }, [getFundTransferAtom?.trans_rec_chart_of_accounts]);

    const currencyOptions = journalAtom?.currency_info || [];
    const selectedType = TRANSFER_TYPES.find(t => t.transaction_type_id === transactionTypeId);
    const isReceive = transactionTypeId === 20;
    const fromLabel = isReceive ? "Receive Account" : "From Account";
    const toLabel = isReceive ? "From Account" : "To Account";

    const onFileSelected = (file) => setAttachmentFile(file);

    const onSubmit = async (data) => {
        const result = await myPromiseModal({
            title: selectedType?.transaction_name,
            body: `Confirm ${isEditMode ? 'update of' : 'creation of'} this ${selectedType?.transaction_name?.toLowerCase()}?`,
            showInput: false,
        });
        if (!result?.decision) return;

        setSaving(true);
        try {
            const formData = new FormData();
            formData.set('journal_date', data.journal_date);
            formData.set('reference_number', data.reference_number || '');
            formData.set('from_account_id', data.from_account_id || 0);
            formData.set('to_account_id', data.to_account_id || 0);
            formData.set('notes', data.notes || '');
            formData.set('currency_id', data.currency_id || 0);
            formData.set('amount', data.amount || 0);
            formData.set('option', isEditMode ? 2 : 1);
            if (isEditMode) formData.set('account_ref_no', fundTransferEdit.account_ref_no);
            if (attachmentFile) formData.set('file', attachmentFile);
            else if (existingAttachmentUrl) formData.set('attachment_url', existingAttachmentUrl);

            const submitFn = isReceive
                ? bankingService.receiveFromAnotherAccount
                : bankingService.transferToAnotherAccount;

            const res = await submitFn(formData);
            const { message_text, message_id } = res.data?.[0] || res.data?.result_status?.[0] || {};

            if (message_id === 1) {
                logicContext?.showToast?.(message_text, "success");
                reset(emptyFormValues);
                setAttachmentFile(null);
                setExistingAttachmentUrl('');
                setFileKey(prev => prev + 1);
                navigate(-1);
            } else {
                logicContext?.showToast?.(message_text, "error");
            }
        } catch (err) {
            console.error('Failed to save fund transfer', err);
            logicContext?.showToast?.("Failed to save fund transfer", "error");
        } finally {
            setSaving(false);
        }
    };

    return (
        <section className="mt-2">
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm max-w-3xl mx-auto">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
                    <div>
                        <p className="text-xs font-semibold text-gray-400 uppercase tracking-widest mb-0.5">
                            {isEditMode ? 'Edit' : 'New'} Fund Transfer
                        </p>
                        <h1 className="text-lg font-bold text-gray-900">
                            {selectedType?.transaction_name}
                        </h1>
                    </div>
                </div>

                <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-5">
                    {/* Type selector */}
                    <FormField label="Transaction Type" required>
                        <div className="flex gap-3">
                            {TRANSFER_TYPES.map(type => (
                                <button
                                    key={type.transaction_type_id}
                                    type="button"
                                    disabled={isEditMode}
                                    onClick={() => setTransactionTypeId(type.transaction_type_id)}
                                    className={`flex-1 px-3 py-2.5 text-sm font-medium rounded-lg border transition ${
                                        transactionTypeId === type.transaction_type_id
                                            ? 'bg-blue-600 text-white border-blue-600'
                                            : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
                                    } ${isEditMode ? 'opacity-60 cursor-not-allowed' : ''}`}
                                >
                                    {type.transaction_name}
                                </button>
                            ))}
                        </div>
                    </FormField>

                    {/* Date | Reference Number */}
                    <div className="grid grid-cols-2 gap-4">
                        <FormField label="Date" required error={errors.journal_date?.message}>
                            <input
                                type="date"
                                className={inputCls}
                                {...register('journal_date', { required: 'Date is required' })}
                            />
                        </FormField>

                        <FormField label="Reference #">
                            <input
                                type="text"
                                placeholder="e.g. REF-0042"
                                className={inputCls}
                                {...register('reference_number')}
                            />
                        </FormField>
                    </div>

                    {/* From/Receive Account | To Account */}
                    <div className="grid grid-cols-2 gap-4">
                        <FormField label={fromLabel} required error={errors.from_account_id?.message}>
                            <select
                                className={selectCls}
                                {...register('from_account_id', { required: `${fromLabel} is required` })}
                            >
                                <option value="">Select account</option>
                                {groupedAccountOptions.map(group => (
                                    <optgroup key={group.label} label={group.label}>
                                        {group.accounts.map(acc => (
                                            <option key={acc.account_id} value={acc.account_id}>
                                                {acc.account_name}
                                            </option>
                                        ))}
                                    </optgroup>
                                ))}
                            </select>
                        </FormField>

                        <FormField label={toLabel} required error={errors.to_account_id?.message}>
                            <select
                                className={selectCls}
                                {...register('to_account_id', { required: `${toLabel} is required` })}
                            >
                                <option value="">Select account</option>
                                {groupedAccountOptions.map(group => (
                                    <optgroup key={group.label} label={group.label}>
                                        {group.accounts.map(acc => (
                                            <option key={acc.account_id} value={acc.account_id}>
                                                {acc.account_name}
                                            </option>
                                        ))}
                                    </optgroup>
                                ))}
                            </select>
                        </FormField>
                    </div>

                    {/* Currency | Amount */}
                    <div className="grid grid-cols-2 gap-4">
                        <FormField label="Currency" required error={errors.currency_id?.message}>
                            <select
                                className={selectCls}
                                {...register('currency_id', { required: 'Currency is required' })}
                            >
                                <option value="">Select currency</option>
                                {currencyOptions.length > 0
                                    ? currencyOptions.map(c => (
                                        <option key={c.currency_id} value={c.currency_id}>
                                            {c.currency_name || c.currency_code}
                                        </option>
                                    ))
                                    : <option value="1">NGN - Nigerian Naira</option>
                                }
                            </select>
                        </FormField>

                        <FormField label="Amount" required error={errors.amount?.message}>
                            <input
                                type="number"
                                step="0.01"
                                min="0"
                                placeholder="0.00"
                                className={inputCls}
                                {...register('amount', { required: 'Amount is required', min: { value: 0.01, message: 'Amount must be greater than 0' } })}
                            />
                        </FormField>
                    </div>

                    {/* Notes */}
                    <FormField label="Notes">
                        <textarea
                            rows={3}
                            maxLength={500}
                            placeholder="Optional notes…"
                            className={`${inputCls} resize-none`}
                            {...register('notes')}
                        />
                    </FormField>

                    {/* Attachment */}
                    <FormField label="Attachment">
                        {existingAttachmentUrl && (
                            <div className="flex items-center gap-2 mb-2 px-3 py-2 bg-blue-50 border border-blue-100 rounded-lg">
                                <a
                                    href={existingAttachmentUrl}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="flex-1 text-xs text-blue-600 underline truncate"
                                >
                                    {existingAttachmentUrl.split('/').pop()}
                                </a>
                                <button
                                    type="button"
                                    onClick={() => setExistingAttachmentUrl('')}
                                    className="w-5 h-5 flex items-center justify-center rounded-full bg-red-100 text-red-500 hover:bg-red-200 transition flex-shrink-0"
                                    title="Remove attachment"
                                >
                                    &times;
                                </button>
                            </div>
                        )}
                        <GeneralFileUploadPreview key={fileKey} onFileSelected={onFileSelected} />
                        <p className="text-xs text-gray-400 mt-1">Maximum file size 2MB</p>
                    </FormField>

                    {/* Footer */}
                    <div className="flex gap-3 pt-4 border-t border-gray-100">
                        <button
                            type="submit"
                            disabled={saving}
                            className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white text-sm font-semibold py-2.5 px-4 rounded-lg transition shadow-sm"
                        >
                            {saving ? 'Saving…' : isEditMode ? 'Update' : 'Save & Publish'}
                        </button>
                        <button
                            type="button"
                            onClick={() => navigate(-1)}
                            className="flex-1 bg-white hover:bg-gray-100 text-gray-700 text-sm font-semibold py-2.5 px-4 rounded-lg border border-gray-200 transition"
                        >
                            Cancel
                        </button>
                    </div>
                </form>
            </div>
        </section>
    );
}

export default AddFundTransferPage;
